import { useState } from 'react';
import { userStore } from '../../stores/user.store';
import { observer } from 'mobx-react-lite';
import { Lock, AlertTriangle, Save, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';

const ForcePasswordResetModal = observer(() => {
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showOld, setShowOld] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);

    if (!userStore.showForceResetModal) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (newPassword !== confirmPassword) {
            toast.error("Passwörter stimmen nicht überein");
            return;
        }

        if (newPassword.length < 5) {
            toast.error("Passwort muss mindestens 5 Zeichen lang sein");
            return;
        }

        setLoading(true);
        const res = await userStore.changePassword(oldPassword, newPassword);
        setLoading(false);

        if (res.success) {
            toast.success("Passwort erfolgreich geändert!");
        } else {
            toast.error(res.message || "Fehler beim Ändern des Passworts");
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
            <div className="bg-surface-2 border border-red-500/50 rounded-2xl p-8 max-w-md w-full shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-red-500 animate-pulse"></div>
                
                <div className="flex flex-col items-center text-center mb-6">
                    <div className="w-16 h-16 bg-red-900/20 rounded-full flex items-center justify-center mb-4 text-red-500 border border-red-500/20">
                        <Lock size={32} />
                    </div>
                    <h2 className="text-2xl font-bold text-white">Passwort ändern erforderlich</h2>
                    <p className="text-fg-muted mt-2 text-sm">
                        Dein Account verwendet ein temporäres Passwort. Bitte setze ein neues, sicheres Passwort, um fortzufahren.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-xs uppercase text-fg-muted font-bold mb-1">Aktuelles Passwort</label>
                        <div className="relative">
                            <input
                                type={showOld ? "text" : "password"}
                                value={oldPassword}
                                onChange={(e) => setOldPassword(e.target.value)}
                                className="w-full bg-ink border border-line rounded-lg p-3 pr-10 text-white focus:border-red-500 focus:outline-none transition-colors"
                                placeholder="Altes Passwort eingeben"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowOld(!showOld)}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-fg-muted hover:text-white transition-colors"
                            >
                                {showOld ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs uppercase text-fg-muted font-bold mb-1">Neues Passwort</label>
                        <div className="relative">
                            <input
                                type={showNew ? "text" : "password"}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="w-full bg-ink border border-line rounded-lg p-3 pr-10 text-white focus:border-blue-500 focus:outline-none transition-colors"
                                placeholder="Neues Passwort"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowNew(!showNew)}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-fg-muted hover:text-white transition-colors"
                            >
                                {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs uppercase text-fg-muted font-bold mb-1">Bestätigen</label>
                        <div className="relative">
                            <input
                                type={showConfirm ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full bg-ink border border-line rounded-lg p-3 pr-10 text-white focus:border-blue-500 focus:outline-none transition-colors"
                                placeholder="Passwort wiederholen"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirm(!showConfirm)}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-fg-muted hover:text-white transition-colors"
                            >
                                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-lg flex items-center justify-center transition-all mt-4"
                    >
                        {loading ? 'Speichere...' : <><Save size={18} className="mr-2" /> Passwort Speichern</>}
                    </button>
                </form>
            </div>
        </div>
    );
});

export default ForcePasswordResetModal;
